import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Target } from "lucide-react";

export function AssignDrillDialog({ rep, personas, onClose, onAssigned }) {
  const [pid, setPid] = useState(rep?.weakest_persona_id || "");
  const [target, setTarget] = useState(75);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!pid) return toast.error("Pick a customer type");
    setBusy(true);
    try {
      const r = await api.post(`/manager/reps/${rep.id}/assign`, { persona_id: pid, target_score: Number(target), note: note.trim() || null });
      toast.success(`Drill assigned to ${rep.name}`); onAssigned(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign that drill"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rep} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="assign-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-rose-600" /> Assign a drill to {rep?.name}</DialogTitle>
          <DialogDescription>It shows on their dashboard and completes automatically once they hit the target score{rep?.weakest_skill ? ` — their weakest skill right now is ${rep.weakest_skill}.` : "."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Customer type</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="assign-persona-grid">
              {personas.map((p) => (
                <button key={p.id} onClick={() => setPid(p.id)} className={`flex items-center gap-2 rounded-xl border p-2 text-left transition-colors ${pid === p.id ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`assign-persona-${p.id}`}>
                  <img src={p.avatar} alt="" className="w-8 h-8 rounded-lg object-cover object-top shrink-0" />
                  <span className="text-xs font-semibold text-slate-800 leading-tight">{p.title}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Target score</span>
              <Input type="number" min={40} max={100} value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1" data-testid="assign-target-input" />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Why (optional)</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={240} placeholder="e.g. Work on asking before pitching" className="mt-1" data-testid="assign-note-input" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="assign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="assign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign drill"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
